import type { ReplayFxEventDetail } from '../replayFx'

export type ReplayFxMode = 'idle' | 'boot' | 'loading' | 'prefetch' | 'prewarm' | 'settle' | 'launch' | 'seek' | 'impact' | 'end'
export type ReplayFxFlashTone = 'cold' | 'hot' | 'white'

export type ReplayFxCueStep = {
  delayMs: number
  mode?: ReplayFxMode
  clearFlashes?: boolean
  flashes?: {
    count: number
    tone: ReplayFxFlashTone
  }
  skyPortalMs?: number
}

export type ReplayFxCue = {
  interrupt?: boolean
  steps: ReplayFxCueStep[]
}

const step = (delayMs: number, cueStep: Omit<ReplayFxCueStep, 'delayMs'>): ReplayFxCueStep => ({
  delayMs,
  ...cueStep,
})

// ── The trimmed-down cue map ─────────────────────────────────────────────
// Everything except three moments is OFF (per the July review with the
// per-layer inventory): the CRT "TV turn-on" when a file starts loading, a
// slow darkening while it loads (the DOM loader speaks over it), and the
// eye-opening shutter release when playback launches. No lightning flashes,
// no seek/damage/prefetch scenes. The WebGL sky portal is untouched — it
// still cycles from the first play.
export const resolveReplayFxCue = (detail: ReplayFxEventDetail): ReplayFxCue => {
  switch (detail.type) {
    case 'file-loading':
      // TV turns on: shutters snap shut + the tube line blooms, then the
      // scene settles into the gradual loading darkening.
      return {
        interrupt: true,
        steps: [
          step(0, { mode: 'boot', clearFlashes: true, skyPortalMs: 0 }),
          step(1280, { mode: 'loading' }),
        ],
      }
    case 'file-ready':
      // stay in the loading darkening — the eyes open at playback start
      return {
        steps: [
          step(0, { mode: 'loading' }),
        ],
      }
    // Startup internals no longer have their own scenes — the loading
    // darkening simply holds until launch.
    case 'startup-prefetch-start':
    case 'startup-prefetch-ready':
    case 'prewarm-start':
    case 'prewarm-ready':
    case 'startup-settle':
      return { steps: [] }
    case 'playback-start':
      if (detail.repeat) {
        // re-pressing play: only the sky tear — no CSS ceremony
        return {
          interrupt: true,
          steps: [
            step(0, { mode: 'idle', clearFlashes: true, skyPortalMs: 7000 }),
          ],
        }
      }
      // File is in — the eyes open (shutter release), then the sky tears
      // open right as the scene clears.
      return {
        interrupt: true,
        steps: [
          step(0, { mode: 'launch', clearFlashes: true }),
          step(detail.durationMs ?? 1480, { mode: 'idle', clearFlashes: true, skyPortalMs: 7000 }),
        ],
      }
    case 'playback-end':
    case 'clear':
      // No shutter-close ceremony — just fade straight to idle and close the
      // portal. The "end" CRT scene was removed per user request.
      return {
        interrupt: true,
        steps: [
          step(0, { mode: 'idle', clearFlashes: true, skyPortalMs: 0 }),
        ],
      }
    case 'seek-start':
    case 'seek-ready':
    case 'damage':
      // effects retired — playback stays clean (the portal keeps its own cycle)
      return {
        interrupt: true,
        steps: [
          step(0, { mode: 'idle', clearFlashes: true }),
        ],
      }
  }
}
